
import React from "react";
import ReactDOM from "react-dom/client";
import { i18nManager } from "../../../../i18n";
import { useSearchParams } from "react-router-dom";
import { Subscriber, Utils } from "../../../../utils";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../shared";
import { ButtonManageRef, Classname, ObjectHelper, Scheme } from "@rivtn/noseur";
import { ApiResponseData, BaseService, ILanguage, IProfile, LanguageService, SanitizedResponse } from "../../../../services";

function Languages() {
    const languageService = LanguageService.getInstance();
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const labels = i18nManager.Labels.dashboard.ploutous.others.languages;
    const [activeLanguage, setActiveLanguage] = React.useState<ILanguage>();
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const [languages, setLanguages] = React.useState<ApiResponseData<ILanguage>>({});

    React.useEffect(fetchLanguages, []);

    return SidedContainer({
        searchParams,
        data: languages,
        setSearchParams,
        onAction: manageLanguage,
        queryData: fetchLanguages,
        labels: labels.sided_labels,
        activeData: (activeLanguage ? {
            data: activeLanguage,
            element: (<React.Fragment>
                <div className="header">
                    <i className="fa fa-times" onClick={() => {
                        setActiveLanguage(undefined);
                        Utils.updateSearchParams("active_data_id", undefined, setSearchParams);
                    }} />
                    <span className="name">{activeLanguage?.name}</span>
                    <span className="id" style={{ fontWeight: "bold" }}>{activeLanguage?.external_id}</span>
                    <span className="id">{activeLanguage?.code}</span>
                </div>
                <div className="middle actions" style={{ marginBottom: 0 }}>
                    <span style={{ marginTop: 5 }} onClick={() => manageLanguage(activeLanguage)}><b>{i18nManager.Labels.dashboard.ploutous.created_by}:</b> {activeLanguage?.created_by?.first_name} {activeLanguage?.created_by?.last_name} ({activeLanguage?.created_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageLanguage(activeLanguage)}><b>{i18nManager.Labels.dashboard.ploutous.updated_by}:</b> {activeLanguage?.updated_by?.first_name} {activeLanguage?.updated_by?.last_name} ({activeLanguage?.updated_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageLanguage(activeLanguage)}><b>{i18nManager.Labels.dashboard.ploutous.created_at}:</b> {Utils.formatToFineDate5(activeLanguage?.created_at)}</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageLanguage(activeLanguage)}><b>{i18nManager.Labels.dashboard.ploutous.updated_at}:</b> {Utils.formatToFineDate5(activeLanguage?.updated_at)}</span>
                </div>
                <div className="actions" style={{ overflow: "auto" }}>
                    <span className="link" onClick={() => manageLanguage(activeLanguage)}>{i18nManager.Labels.dashboard.ploutous.edit}</span>
                    {activeLanguage?.status !== "ACTIVE" && (<span className="link" onClick={() => activateDeactivateLanguage(activeLanguage, true)}>{i18nManager.Labels.dashboard.ploutous.activate}</span>)}
                    {activeLanguage?.status === "ACTIVE" && (<span className="link danger" onClick={() => activateDeactivateLanguage(activeLanguage, false)}>{i18nManager.Labels.dashboard.ploutous.deactivate}</span>)}
                    {activeLanguage?.status !== "DELETED" && (<span className="link danger" onClick={() => deleteLanguage(activeLanguage)}>{i18nManager.Labels.dashboard.ploutous.delete}</span>)}
                </div>
            </React.Fragment>)
        } : undefined),
        dataTemplate: (language: ILanguage) => {
            return (<div className={Classname.build("channel", (activeLanguage?.external_id === language.external_id ? "active" : null))} onClick={() => {
                setActiveLanguage(language);
                Utils.updateSearchParams("active_data_id", language.external_id, setSearchParams);
            }}>
                <div className="id" style={language.status === "DELETED" ? { textDecoration: "line-through" } : {}}>
                    <i className="fa fa-edit link gray" style={{ marginRight: 10 }} onClick={() => manageLanguage(language)} />
                    <i className={Classname.build("fa fa-trash link danger", (language.status === "DELETED" ? "noseur-disabled" : null))} style={{ marginRight: 10 }} onClick={() => deleteLanguage(language)} />
                    <span className="name">{language.name}</span>
                    <span className="type"> - {language.code}</span>
                </div>
            </div>);
        }
    });

    function fetchLanguages() {
        const id = searchParams.get("active_data_id");
        languageService.queryLanguages(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<ILanguage>) => {
            setLanguages(apiResponse.data!);
            if (id) for (const item of apiResponse.data?.content!) {
                if (item.external_id === id) {
                    setActiveLanguage(item);
                    break;
                }
            }
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function manageLanguage(language?: ILanguage) {
        const payload = ObjectHelper.clone(language ?? {} as any);
        const basics = labels.dialog.manage.basic[language ? "update" : "create"];
        const dialog = ConfirmDialog({
            ...basics,
            closable: true,
            rightStyle: { flex: 1.5 },
            confirmLabel: basics.confirm_label,
            style: { minWidth: 400, maxWidth: "80%" },
            content: (<div className="add-new-role">
                {Elements.buildConfirmDialogForm(labels.dialog.manage.form, (name: string, value: string) => {
                    dialogErrorRef.current!.innerText = " ";
                    payload[name] = value;
                    return false;
                }, payload, undefined, { marginTop: 0 })}
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                let errorMessage;
                dialogErrorRef.current!.innerText = " ";
                const formattedPayload = ObjectHelper.clone(payload);
                if (Object.keys(formattedPayload).length < 3) {
                    errorMessage = labels.dialog.manage.fill_required_fields;
                }
                if (errorMessage) {
                    dialogErrorRef.current!.innerText = errorMessage;
                    return false;
                }
                button?.setLoadingState(true);
                const method = (!!language ? languageService.updateLanguage : languageService.createLanguage).bind(languageService);
                method(formattedPayload)
                    .then(({ }: SanitizedResponse<ILanguage>) => {
                        Siren.alert(basics.successful);
                        dialog.destroy(fetchLanguages);
                    }).catch(({ errors }: SanitizedResponse<any>) => {
                        dialogErrorRef.current!.innerText = errors?.join("\n") ?? "";
                    }).finally(() => {
                        button?.setLoadingState(false);
                    });
                return false;
            }
        });
        dialog.show();
    }

    function activateDeactivateLanguage(language: ILanguage, activate: boolean) {
        languageService.updateLanguage({ external_id: language.external_id, status: (activate ? "ACTIVE" : "IN_ACTIVE") }).then(() => {
            Siren.alert(activate ? labels.successfully_activate : labels.successfully_deactivate);
            fetchLanguages();
        }).catch(BaseService.reportError);
    }

    function deleteLanguage(language: ILanguage) {
        const dialog = ConfirmDialog({
            closable: true,
            title: labels.dialog.delete.title,
            desc: ObjectHelper.expandStringTemplate(labels.dialog.delete.desc, { name_code: `${language.name} (${language.code})` }, { chop: "$" }),
            confirmScheme: Scheme.DANGER,
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                languageService.deleteLanguage(language.external_id).then(() => {
                    Siren.alert(labels.dialog.delete.successful);
                    setActiveLanguage(undefined);
                    dialog.destroy(() => {
                        fetchLanguages();
                    });
                }).catch(BaseService.reportError).finally(() => button?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

}

export default Languages;

